import { z } from 'zod';
import { EventStatus, EventVisibility, Department } from '../enums';

/** Hard ceiling on generated recurrence instances regardless of the rule. */
export const RECURRENCE_MAX_INSTANCES = 366;

export const RecurrenceFrequencySchema = z.enum([
  'daily',
  'weekly',
  'monthly_date',
  'monthly_weekday',
]);

export const RecurrenceEndSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('count'), count: z.number().int().min(1).max(RECURRENCE_MAX_INSTANCES) }),
  z.object({ type: z.literal('until'), until: z.string() }),
]);

export const RecurrenceRuleSchema = z.object({
  freq: RecurrenceFrequencySchema,
  interval: z.number().int().min(1).max(52).default(1),
  byWeekday: z.array(z.number().int().min(0).max(6)).optional(), // 0=Sun..6=Sat (weekly)
  end: RecurrenceEndSchema,
});

/** Scope for editing/deleting an instance of a recurring series. */
export const EventScopeSchema = z.enum(['this', 'following', 'all']);

export type RecurrenceFrequency = z.infer<typeof RecurrenceFrequencySchema>;
export type RecurrenceEnd = z.infer<typeof RecurrenceEndSchema>;
export type RecurrenceRule = z.infer<typeof RecurrenceRuleSchema>;
export type EventScope = z.infer<typeof EventScopeSchema>;

export const CreateEventSchema = z.object({
  event_type_id: z.string().uuid(),
  title: z.string().max(255).default(''),
  description: z.string().optional(),
  // Rich-text note document (TipTap ProseMirror JSON). `description` holds the
  // derived plain text for previews/search/.ics.
  description_doc: z.any().nullable().optional(),
  // Nullable/optional: a task with no date is a backlog item.
  start_at: z.string().datetime().nullable().optional(),
  end_at: z.string().datetime().optional(),
  all_day: z.boolean().default(false),
  location: z.string().optional(),
  contact_name: z.string().max(100).optional(),
  contact_phone: z.string().max(50).optional(),
  status: z.nativeEnum(EventStatus).default(EventStatus.SCHEDULED),
  visibility: z.nativeEnum(EventVisibility).default(EventVisibility.TEAM),
  progress: z.number().int().min(0).max(100).nullable().optional(),
  project_id: z.string().uuid().nullable().optional(),
  client_id: z.string().uuid().nullable().optional(),
  assigned_to: z.string().uuid().optional(),
  metadata: z.record(z.any()).default({}),
  departments: z.array(z.nativeEnum(Department)).nullable().optional(),
  // When set, the event is created as a recurring series of materialized instances.
  recurrence: RecurrenceRuleSchema.optional(),
});

export const UpdateEventSchema = CreateEventSchema.partial().omit({
  event_type_id: true,
  recurrence: true,
});

export const RescheduleEventSchema = z.object({
  start_at: z.string().datetime(),
  end_at: z.string().datetime().optional(),
  all_day: z.boolean().optional(),
});

export const EventQuerySchema = z.object({
  start: z.string(),
  end: z.string(),
  view: z.enum(['calendar', 'full']).default('full'),
  type: z.string().optional(),
  project_id: z.string().uuid().optional(),
  assigned_to: z.string().uuid().optional(),
  status: z.string().optional(),
  status_group: z.enum(['not_complete']).optional(),
  department: z.nativeEnum(Department).optional(),
  // Query params arrive as strings; accept 'true'/'false' explicitly so an
  // omitted param stays undefined (z.coerce.boolean would treat 'false' as true).
  task_only: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  undated: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(500).default(100),
});

export type CreateEvent = z.infer<typeof CreateEventSchema>;
export type UpdateEvent = z.infer<typeof UpdateEventSchema>;
export type RescheduleEvent = z.infer<typeof RescheduleEventSchema>;
export type EventQuery = z.infer<typeof EventQuerySchema>;
